<template>
  <section class="commander-screen">
    <header class="commander-topbar">
      <div class="commander-brand">
        <router-link to="/dashboard" class="round-icon" title="Inicio">⌂</router-link>
        <div>
          <strong>Comandero</strong>
          <small>{{ contextLabel }}</small>
        </div>
      </div>

      <button class="order-chip" :class="{ empty: !activeTicket }" @click="handleOrderChipClick">
        <span>{{ activeTicket ? 'Orden activa' : 'Crear orden' }}</span>
        <strong>{{ activeTicket ? ticketLabel(activeTicket) : 'Para llevar · Mesa · Cliente / QR' }}</strong>
      </button>

      <div class="commander-actions">
        <button class="accounts-nav-btn" title="Mis cuentas" aria-label="Mis cuentas" @click="openAccountsModal">
          <span class="accounts-icon">📋</span>
          <span class="accounts-text">Mis cuentas</span>
          <strong>{{ tickets.length }}</strong>
        </button>
        <PosSyncStatus />
        <span class="status-pill" :class="cashSession ? 'ok' : 'warn'">{{ multiRegisterEnabled ? (registerName || 'Elegir caja') : (cashSession ? 'Caja abierta' : 'Sin caja') }}</span>
        <router-link class="pos-btn ghost" to="/pv">PV</router-link><router-link class="pos-btn ghost" to="/pv/equipo">Equipo</router-link>
        <button class="pos-btn ghost" @click="loadAll">Actualizar</button>
      </div>
    </header>

    <div class="sticky-search-wrap">
      <div class="search-box">
        <span>⌕</span>
        <input
          v-model="search"
          :placeholder="activeCategory ? `Buscar en ${activeCategory.name}` : 'Buscar producto en todas las categorías'"
        />
        <button v-if="search" aria-label="Borrar búsqueda" @click="clearSearch">×</button>
      </div>
      <div class="search-context">
        <strong>{{ activeCategory ? activeCategory.name : 'Categorías' }}</strong>
        <small v-if="activeCategory">Busqueda dentro de la categoría seleccionada</small>
        <small v-else>Toca una categoría o escribe para buscar directo</small>
      </div>
    </div>

    <div v-if="message" class="commander-toast" :class="messageType">{{ message }}</div>

    <div v-if="!cashSession" class="cash-blocker">
      <section class="cash-card">
        <span class="status-pill warn">Caja requerida</span>
        <h1>{{ !registerStatusChecked ? 'Verifica la conexión y las cajas' : (multiRegisterEnabled && !selectedRegisterId ? 'Selecciona la caja de este dispositivo' : 'Abre caja desde el punto de venta') }}</h1>
        <p v-if="multiRegisterEnabled">Cada terminal utiliza su propia caja. Las órdenes requieren conexión mientras el piloto multicaja esté activo.</p>
        <p v-else>El comandero crea cuentas reales del PV, por eso necesita una caja abierta antes de tomar órdenes.</p>
        <div v-if="multiRegisterEnabled && !selectedRegisterId" class="cash-actions">
          <button v-for="entry in registerOptions" :key="entry.id" class="pos-btn ghost" @click="chooseRegister(entry.id)">{{ entry.name }}</button>
        </div>
        <div class="cash-actions">
          <router-link class="pos-btn primary" to="/pv">Ir al PV</router-link>
          <button class="pos-btn ghost" @click="loadCash">Revisar otra vez</button>
        </div>
      </section>
    </div>

    <main class="commander-main" :class="{ blocked: !cashSession }">
      <section v-if="!activeCategory && !search" class="category-grid">
        <button
          v-for="category in categoriesWithCounts"
          :key="category.id"
          class="category-tile" :style="tileStyle(category)"
          @click="activeCategory = category"
        >
          <span>{{ category.name }}</span>
          <strong>{{ category.count }}</strong>
          <small>producto(s)</small>
        </button>
        <div v-if="!categoriesWithCounts.length" class="empty-state">No hay categorías disponibles para PV.</div>
      </section>

      <section v-else class="product-grid">
        <button
          v-for="product in visibleProducts"
          :key="product.id"
          class="product-tile" :style="tileStyle(product)" :disabled="stockBlocked(product) || posBusy.addProductFromModal"
          :class="{ extra: product.is_virtual_extra, 'stock-empty': stockEmpty(product) }"
          @click="selectProduct(product)"
        >
          <span>
            <strong>{{ product.name }}</strong>
            <small>{{ product.is_virtual_extra ? 'Extra independiente' : (product.category_name || 'Sin categoría') }}</small>
          </span>
          <em>{{ money(product.sale_price) }}</em><small v-if="hasStock(product)" class="stock-label">{{ Number(product.stock_available || 0) <= 0 ? "Agotado · " + product.stock_available : product.stock_available + " disponibles" }}</small>
        </button>
        <div v-if="!visibleProducts.length" class="empty-state">No hay productos con este filtro.</div>
      </section>
    </main>

    <div class="floating-stack">
      <button v-if="activeCategory" class="floating-back" @click="backToCategories">← Categorías</button>
      <button class="floating-next" :class="{ empty: !activeItems.length }" @click="openSummaryOrOrder">
        <span>{{ activeItems.length || 0 }}</span>
        <strong>➜</strong>
        <small>{{ activeTicket ? money(activeTicket.total || summaryTotal) : 'Nueva orden' }}</small>
      </button>
    </div>

    <div v-if="showOrderModal" class="modal-backdrop" @click.self.stop.prevent>
      <section class="modal new-ticket-modal">
        <div class="modal-head">
          <div>
            <small>Nueva orden</small>
            <h2>¿Cómo se atenderá?</h2>
          </div>
          <button class="round-icon" @click="closeOrderModal">×</button>
        </div>

        <div class="service-picker">
          <button class="service-card" :class="{ active: newTicketMode === 'takeaway' }" @click="selectNewTicketMode('takeaway')">
            <strong>Para llevar</strong>
            <small>Mostrador rápido</small>
          </button>
          <button class="service-card" :class="{ active: newTicketMode === 'onsite' }" @click="selectNewTicketMode('onsite')">
            <strong>Mesa</strong>
            <small>Elegir mesa en un toque</small>
          </button>
          <button class="service-card" :class="{ active: newTicketMode === 'customer' }" @click="selectNewTicketMode('customer')">
            <strong>Cliente / QR</strong>
            <small>Buscar o escanear lealtad</small>
          </button>
        </div>

        <div v-if="newTicketMode === 'takeaway'" class="service-flow">
          <label>
            <span>Nombre opcional para llamar la orden</span>
            <input v-model="newTicketForm.order_label" placeholder="Ej. Juan, pedido mostrador..." @keyup.enter="startTakeawayTicket" />
          </label>
          <button :aria-busy="posBusy.createTicket || undefined" :disabled="posBusy.createTicket" class="pos-btn primary full" @click="startTakeawayTicket">Crear orden para llevar</button>
        </div>

        <div v-if="newTicketMode === 'onsite'" class="service-flow">
          <div class="table-grid touch-grid">
            <button :aria-busy="posBusy.createTicket || undefined" :disabled="posBusy.createTicket"
              v-for="table in tables"
              :key="table.id"
              class="table-card"
              :class="table.status"
              @click="startTableTicket(table)"
            >
              <strong>{{ table.name }}</strong>
              <small>{{ table.status === 'occupied' ? 'Ocupada / abrir' : table.status }}</small>
            </button>
            <div v-if="!tables.length" class="empty-state">No hay mesas registradas.</div>
          </div>
          <div class="inline-form">
            <input v-model="newTable.name" placeholder="Nueva mesa" />
            <input v-model.number="newTable.capacity" type="number" min="1" placeholder="Cap." />
            <button class="pos-btn primary" @click="createTable">Crear</button>
          </div>
        </div>

        <div v-if="newTicketMode === 'customer'" class="service-flow">
          <div class="customer-search">
            <input v-model="customerSearch" placeholder="Nombre, teléfono, correo o QR" @keyup.enter="loadCustomers" />
            <button class="pos-btn ghost" @click="lookupCustomerQrForNewTicket">QR</button>
          </div>
          <div class="customer-list touch-list">
            <button :aria-busy="posBusy.createTicket || undefined" :disabled="posBusy.createTicket" v-for="customer in customers" :key="customer.id" class="customer-card" @click="startCustomerTicket(customer)">
              <strong>{{ customer.name }}</strong>
              <small>{{ customer.email || customer.phone || customer.qr_token }}</small>
              <span>{{ formatPoints(customer.points_balance || 0) }} pts</span>
            </button>
            <div v-if="!customers.length" class="empty-state">Busca o crea un cliente.</div>
          </div>
          <div class="new-customer">
            <strong>Alta rápida</strong>
            <input v-model="newCustomer.name" placeholder="Nombre" />
            <input v-model="newCustomer.phone" placeholder="Teléfono" />
            <input v-model="newCustomer.email" placeholder="Correo" />
            <button class="pos-btn primary" @click="createCustomerFromNewTicket">Crear orden con cliente</button>
          </div>
        </div>
      </section>
    </div>

    <div v-if="showAccountsModal" class="modal-backdrop" @click.self.stop.prevent>
      <section class="modal accounts-modal">
        <div class="modal-head">
          <div>
            <small>Cuentas abiertas</small>
            <h2>Mesas y órdenes activas</h2>
          </div>
          <button class="round-icon" @click="showAccountsModal = false">×</button>
        </div>

        <div class="accounts-toolbar">
          <div class="search-box compact">
            <span>⌕</span>
            <input v-model="accountsSearch" placeholder="Buscar mesa, cliente o ticket" />
            <button v-if="accountsSearch" aria-label="Borrar búsqueda" @click="accountsSearch = ''">×</button>
          </div>
          <button class="pos-btn ghost" @click="loadTickets">Actualizar</button>
        </div>

        <div class="accounts-list">
          <article
            v-for="ticket in visibleOpenTickets"
            :key="ticket.id"
            class="account-card"
            :class="{ active: activeTicket && Number(activeTicket.id) === Number(ticket.id) }"
          >
            <header>
              <div>
                <strong>{{ ticketLabel(ticket) }}</strong>
                <small>{{ ticket.ticket_number }} · {{ shortTime(ticket.created_at) }}</small>
              </div>
              <em>{{ money(ticket.total) }}</em>
            </header>
            <div class="account-meta">
              <span>{{ Number(ticket.items_count || 0) }} producto(s)</span>
              <span v-if="Number(ticket.pending_kitchen_items_count || 0)">{{ Number(ticket.pending_kitchen_items_count || 0) }} pendiente(s)</span>
              <span>{{ serviceTypeLabel(ticket.service_type) }}</span>
            </div>
            <div class="account-actions">
              <button class="pos-btn ghost" @click="openExistingTicket(ticket)">Abrir</button>
              <button class="pos-btn primary" @click="requestAccountPrint(ticket)">Solicitar impresión</button>
            </div>
          </article>
          <div v-if="!visibleOpenTickets.length" class="empty-state">No hay cuentas abiertas con este filtro.</div>
        </div>
      </section>
    </div>

    <div v-if="productModal" class="modal-backdrop" @click.self.stop.prevent>
      <section class="modal product-modal">
        <div class="modal-head">
          <div>
            <small>Personalizar</small>
            <h2>{{ productModal.name }}</h2>
          </div>
          <button class="round-icon" @click="closeProductModal">×</button>
        </div>

        <div class="product-config">
          <label>
            <span>Cantidad</span>
            <input v-model.number="productForm.quantity" type="number" min="1" step="1" @keyup.enter="focusNextProductInput" />
          </label>
          <label>
            <span>Nota del producto</span>
            <textarea v-model="productForm.notes" rows="2" placeholder="Ej. sin azúcar, extra caliente..." @keyup.enter.exact.prevent="addProductFromModal"></textarea>
          </label>
        </div>

        <div v-if="modifierPricingRule(productModal)" class="product-pricing-note">
          <strong>{{ includedCountLabel(modifierPricingRule(productModal).included_count) }}</strong>
          <span>Después, cada ingrediente agrega {{ money(modifierPricingRule(productModal).extra_price) }}.</span>
        </div>

        <div v-for="group in productModal.option_groups || []" :key="group.id" class="option-group">
          <div class="option-title">
            <strong>{{ group.name }}</strong>
            <small>{{ group.selection_type === 'multiple' ? 'Puedes elegir varias' : 'Elige una' }}</small>
          </div>
          <div class="option-grid">
            <button
              v-for="option in group.options"
              :key="option.id"
              class="option-chip"
              :class="{ active: optionSelected(group, option), unavailable: option.availability_status === 'sold_out' }"
              :disabled="option.availability_status === 'sold_out'"
              @click="toggleOption(group, option, !optionSelected(group, option))"
            >
              <span>{{ option.name }}</span>
              <small>{{ option.availability_status === 'sold_out' ? 'Agotado' : optionPriceLabel(group, option, productModal) }}</small>
            </button>
          </div>
        </div>

        <div class="modal-total">
          <span>Total producto</span>
          <strong>{{ money(productPreviewTotal) }}</strong>
        </div>

        <button :aria-busy="posBusy.addProductFromModal || undefined" :disabled="posBusy.addProductFromModal" class="pos-btn primary full big-action" @click="addProductFromModal">Agregar a la orden</button>
      </section>
    </div>

    <div v-if="showSummaryModal" class="modal-backdrop" @click.self.stop.prevent>
      <section class="modal summary-modal">
        <div class="modal-head">
          <div>
            <small>Resumen</small>
            <h2>{{ activeTicket ? ticketLabel(activeTicket) : 'Nueva orden' }}</h2>
          </div>
          <button class="round-icon" @click="showSummaryModal = false">×</button>
        </div>

        <div v-if="activeTicket" class="summary-meta">
          <div><span>Tipo</span><strong>{{ serviceTypeLabel(activeTicket.service_type || orderForm.service_type) }}</strong></div>
          <div v-if="activeTicket.table_name"><span>Mesa</span><strong>{{ activeTicket.table_name }}</strong></div>
          <div v-if="activeTicket.customer_name"><span>Cliente</span><strong>{{ activeTicket.customer_name }}</strong></div>
          <div><span>Ticket</span><strong>{{ activeTicket.ticket_number }}</strong></div>
        </div>

        <div class="summary-items">
          <article v-for="item in activeItems" :key="item.id" class="summary-item">
            <div>
              <strong>{{ item.quantity }} × {{ item.name }}</strong>
              <small v-for="modifier in normalizedModifiers(item)" :key="modifier.option_id || modifier.name">{{ modifier.preparation_note || modifier.name }}</small>
              <small v-if="item.notes">Nota: {{ item.notes }}</small>
            </div>
            <div class="summary-item-actions">
              <span>{{ money(item.line_total) }}</span>
              <button :aria-busy="posBusy.removeItemLocal || undefined" :disabled="posBusy.removeItemLocal" @click="removeItemLocal(item)">×</button>
            </div>
          </article>
          <div v-if="!activeItems.length" class="empty-state">Todavía no hay productos en la orden.</div>
        </div>

        <label class="summary-notes">
          <span>Notas generales</span>
          <textarea v-model="orderForm.notes" rows="2" placeholder="Notas para la cuenta" @blur="syncTicketMeta"></textarea>
        </label>

        <div class="summary-total">
          <span>Total estimado</span>
          <strong>{{ money(activeTicket?.total || summaryTotal) }}</strong>
        </div>

        <div class="modal-actions">
          <button class="pos-btn ghost" @click="showSummaryModal = false">Cerrar</button>
          <button class="pos-btn ghost" :disabled="!activeTicket && !activeItems.length" @click="resetOrder">Limpiar</button>
          <button :aria-busy="posBusy.sendOrderCommand || undefined" class="pos-btn primary" :disabled="(sending || !activeTicket || !pendingCommandItems.length) || posBusy.sendOrderCommand" @click="sendOrderCommand">
            {{ sending ? 'Enviando...' : (pendingCommandItems.length ? 'Enviar comanda' : 'Sin nuevos') }}
          </button>
        </div>
      </section>
    </div>
  </section>
</template>

<script>
import api, {requireEmptyQueueBeforeRegisterSwitch,refreshPosPendingStatus} from '@/services/posApi';
import rawApi from '@/services/api';
import {registerIdentity,getSelectedRegister,setSelectedRegister,setMultiRegisterEnabled,isMultiRegisterEnabled,isRegisterModeKnown} from '@/services/pos/registerSelection.mjs';
import posInteraction from '@/services/posInteraction';
import PosSyncStatus from '@/components/pos/PosSyncStatus.vue';
import { promptText, confirmAction } from '@/services/dialog';
import { getSocket, getOrConnectSocket, isSocketConnected } from '@/services/websocket';

const POS_TIME_ZONE = 'America/Mexico_City';

function normalizePosDateString(value) {
  let normalized = String(value || '').trim().replace(' ', 'T');
  normalized = normalized.replace(/\.(\d{3})\d+/, '.$1');
  normalized = normalized.replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
  normalized = normalized.replace(/([+-]\d{2})$/, '$1:00');
  if (!/[zZ]$|[+-]\d{2}:\d{2}$/.test(normalized)) normalized += 'Z';
  return normalized;
}

function parsePosDate(value) {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  const parsed = new Date(normalizePosDateString(value));
  return Number.isNaN(parsed.getTime()) ? new Date(value) : parsed;
}

export default {
  mixins: [posInteraction],
  components: { PosSyncStatus },
  name: 'ComanderoView',
  data() {
    return {
      message: '',
      messageType: 'success',
      cashSession: null,
      multiRegisterEnabled: false,
      registerStatusChecked: false,
      selectedRegisterId: 0,
      registerOptions: [],
      registerName: '',
      categories: [],
      products: [],
      tables: [],
      tickets: [],
      customers: [],
      activeCategory: null,
      search: '',
      activeTicket: null,
      items: [],
      payments: [],
      orderForm: { service_type: 'takeaway', order_label: '', table_id: null, notes: '' },
      productModal: null,
      productForm: { quantity: 1, notes: '', selections: {} },
      showOrderModal: false,
      showSummaryModal: false,
      showAccountsModal: false,
      accountsSearch: '',
      newTicketMode: 'takeaway',
      newTicketForm: { order_label: '' },
      newTable: { name: '', capacity: 4 },
      customerSearch: '',
      newCustomer: { name: '', phone: '', email: '' },
      queuedProduct: null,
      sending: false,
      socketConnected: false,
      socketTimer: null,
    };
  },
  computed: {
    contextLabel() {
      const context = this.$store.state.context;
      const company = context?.company?.name || 'Empresa';
      const branch = context?.branch?.name ? ` · ${context.branch.name}` : '';
      return `${company}${branch}`;
    },
    categoriesWithCounts() {
      return this.categories
        .map((category) => ({ ...category, count: this.products.filter((product) => Number(product.category_id) === Number(category.id)).length }))
        .filter((category) => category.count > 0);
    },
    visibleProducts() {
      const q = this.search.trim().toLowerCase();
      return this.products.filter((product) => {
        const byCategory = this.activeCategory ? Number(product.category_id) === Number(this.activeCategory.id) : true;
        const haystack = `${product.name} ${product.sku || ''} ${product.category_name || ''}`.toLowerCase();
        const bySearch = q ? haystack.includes(q) : true;
        return byCategory && bySearch;
      });
    },
    visibleOpenTickets() {
      const q = this.accountsSearch.trim().toLowerCase();
      return this.tickets.filter((ticket) => {
        if (!q) return true;
        const haystack = `${this.ticketLabel(ticket)} ${ticket.ticket_number || ''} ${ticket.table_name || ''} ${ticket.customer_name || ''}`.toLowerCase();
        return haystack.includes(q);
      });
    },
    activeItems() {
      return this.items.filter((item) => item.status === 'active');
    },
    pendingCommandItems() {
      return this.activeItems.filter((item) => item.kitchen_status !== 'sent');
    },
    summaryTotal() {
      return this.activeItems.reduce((sum, item) => sum + Number(item.line_total || 0), 0);
    },
    productPreviewTotal() {
      if (!this.productModal) return 0;
      return this.productUnitPrice(this.productModal) * Number(this.productForm.quantity || 1);
    },
  },
  mounted() {
    this.loadRegisterOptions();
    this.socketConnected = isSocketConnected();
    this.socketTimer = window.setInterval(() => {
      this.socketConnected = isSocketConnected();
    }, 2000);
  },
  beforeUnmount() {
    if (this.socketTimer) window.clearInterval(this.socketTimer);
  },
  methods: {
    async loadRegisterOptions() {
      try {
        const {data}=await rawApi.get('/api/pos/cash/registers',{timeout:12000});
        const ctx=registerIdentity(this.$store.state);
        const enabled=!!data.multi_register_enabled;
        // Preflight the legacy device queue BEFORE changing its storage namespace.
        // If the server flag was enabled too early, fail closed (no new sales).
        if(enabled && !isMultiRegisterEnabled(ctx)) {
          await requireEmptyQueueBeforeRegisterSwitch();
        }
        setMultiRegisterEnabled(ctx,enabled);
        this.multiRegisterEnabled=enabled;
        this.registerStatusChecked=true;
        this.registerOptions=data.registers||[];
        let id=enabled?getSelectedRegister(ctx):0;
        if(enabled && !this.registerOptions.some(row=>Number(row.id)===id)) {
          if(id) setSelectedRegister(ctx,0);
          id=0;
        }
        this.selectedRegisterId=id;
        this.registerName=this.registerOptions.find(row=>Number(row.id)===id)?.name||'';
        if(!enabled || id) {
          if(enabled)await refreshPosPendingStatus();
          await this.loadAll();
        }
        else this.cashSession=null;
      } catch(error) {
        const ctx=registerIdentity(this.$store.state);
        this.multiRegisterEnabled=isMultiRegisterEnabled(ctx);
        this.registerStatusChecked=error.code==='pos_register_pending_operations'
          ? false : (isRegisterModeKnown(ctx)&&!this.multiRegisterEnabled);
        this.selectedRegisterId=this.multiRegisterEnabled?getSelectedRegister(ctx):0;
        this.showError(error.response?.data?.error||'No fue posible verificar las cajas.');
        if(this.registerStatusChecked)await this.loadAll();
      }
    },
    async chooseRegister(rawId) {
      const id=Number(rawId);
      if(!this.registerOptions.some(row=>Number(row.id)===id))return;
      try {
        await requireEmptyQueueBeforeRegisterSwitch(this.registerOptions.map(row=>row.id));
        setSelectedRegister(registerIdentity(this.$store.state),id);
        window.location.reload();
      }catch(error){this.showError(error.response?.data?.error||error.message||'No se pudo seleccionar caja.');}
    },
    formatPoints(value) {
      const number = Number(value || 0);
      return Number.isInteger(number) ? String(number) : number.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
    },
    async loadAll() {
      if(!this.registerStatusChecked || (this.multiRegisterEnabled&&!this.selectedRegisterId))return;
      await Promise.all([this.loadCash(), this.loadCatalog(), this.loadTables(), this.loadTickets(), this.loadCustomers()]);
    },
    async loadCash() {
      try {
        const { data } = await api.get('/api/pos/cash/current');
        this.cashSession = data.cash_session || null;
      } catch (_error) {
        this.cashSession = null;
      }
    },
    async loadCatalog() {
      const { data } = await api.get('/api/pos/catalog');
      this.categories = data.categories || [];
        this.stockSettings = data.stock_settings || this.stockSettings;
      this.products = data.products || [];
    },
    async loadTables() {
      try {
        const { data } = await api.get('/api/pos/tables');
        this.tables = data.tables || [];
      } catch (_error) {
        this.tables = [];
      }
    },
    async loadTickets() {
      try {
        const { data } = await api.get('/api/pos/tickets', { params: { status: 'open' } });
        this.tickets = data.tickets || [];
      } catch (_error) {
        this.tickets = [];
      }
    },
    async loadTicket(ticketId) {
      const { data } = await api.get(`/api/pos/tickets/${ticketId}`);
      this.activeTicket = data.ticket;
      this.items = data.items || [];
      this.payments = data.payments || [];
      this.orderForm = {
        service_type: data.ticket.service_type || 'takeaway',
        order_label: data.ticket.order_label || '',
        table_id: data.ticket.table_id || null,
        notes: data.ticket.notes || '',
      };
    },
    async loadCustomers() {
      try {
        const params = {};
        if (this.customerSearch) params.q = this.customerSearch;
        const { data } = await api.get('/api/pos/customers', { params });
        this.customers = data.customers || [];
      } catch (_error) {
        this.customers = [];
      }
    },
    async openAccountsModal() {
      await this.loadTickets();
      this.showAccountsModal = true;
    },
    async openExistingTicket(ticket) {
      if (!ticket?.id) return;
      await this.loadTicket(ticket.id);
      this.showAccountsModal = false;
      this.showOrderModal = false;
      this.showSummaryModal = false;
      this.showSuccess('Cuenta abierta en comandero.');
    },
    requestAccountPrint(ticket) {
      if (!ticket?.id) return;
      this.emitModuleEvent('pos.account.print.request', {
        ticket,
        ticket_id: ticket.id,
        origin_channel: 'comandero',
        requested_by: this.$store.state.user?.name || 'Comandero',
        created_at: new Date().toISOString(),
      });
      this.showSuccess('Solicitud de impresión enviada al PV.');
    },
    handleOrderChipClick() {
      if (this.activeTicket) this.showSummaryModal = true;
      else this.openOrderModal();
    },
    openSummaryOrOrder() {
      if (!this.activeTicket) {
        this.openOrderModal();
        return;
      }
      this.showSummaryModal = true;
    },
    openOrderModal() {
      if (!this.cashSession) {
        this.showError('Abre caja antes de crear una orden.');
        return;
      }
      this.newTicketMode = 'takeaway';
      this.newTicketForm = { order_label: '' };
      this.showOrderModal = true;
      this.loadTables();
      this.loadTickets();
      this.loadCustomers();
    },
    closeOrderModal() {
      this.showOrderModal = false;
      this.queuedProduct = null;
    },
    selectNewTicketMode(mode) {
      this.newTicketMode = mode;
      if (mode === 'onsite') this.loadTables();
      if (mode === 'customer') this.loadCustomers();
    },
    async createTicket(payload = {}) {
      try {
        const { data } = await api.post('/api/pos/tickets', { ...payload, origin_channel: 'comandero' });
        await this.loadTicket(data.ticket.id);
        await this.loadTickets();
        await this.loadTables();
        this.showOrderModal = false;
        this.showSuccess('Orden creada.');
        this.emitModuleEvent('pos.ticket.updated', { ticket: data.ticket, origin_channel: 'comandero' });
        const queued = this.queuedProduct;
        this.queuedProduct = null;
        if (queued) this.$nextTick(() => this.selectProduct(queued));
        return true;
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo crear la orden.');
        return false;
      }
    },
    async startTakeawayTicket() {
      await this.createTicket({ service_type: 'takeaway', order_label: this.newTicketForm.order_label || '' });
    },
    async startTableTicket(table) {
      const openTicket = this.tickets.find((ticket) => Number(ticket.table_id) === Number(table.id));
      if (table.status === 'occupied' && openTicket) {
        await this.loadTicket(openTicket.id);
        this.showOrderModal = false;
        const queued = this.queuedProduct;
        this.queuedProduct = null;
        if (queued) this.$nextTick(() => this.selectProduct(queued));
        return;
      }
      await this.createTicket({ service_type: 'onsite', table_id: table.id, order_label: `Mesa ${table.name}` });
    },
    async startCustomerTicket(customer) {
      await this.createTicket({ service_type: 'takeaway', customer_id: customer.id, order_label: customer.name });
    },
    async createTable() {
      if (!this.newTable.name) return;
      try {
        await api.post('/api/pos/tables', this.newTable);
        this.newTable = { name: '', capacity: 4 };
        await this.loadTables();
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo crear mesa.');
      }
    },
    async createCustomerFromNewTicket() {
      if (!this.newCustomer.name) return;
      try {
        const { data } = await api.post('/api/pos/customers', this.newCustomer);
        this.newCustomer = { name: '', phone: '', email: '' };
        await this.loadCustomers();
        await this.startCustomerTicket(data.customer);
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo crear cliente.');
      }
    },
    async lookupCustomerQrForNewTicket() {
      try {
        const { data } = await api.get('/api/pos/customers/lookup', { params: { qr: this.customerSearch } });
        this.customers = [data.customer];
        await this.startCustomerTicket(data.customer);
      } catch (error) {
        this.showError(error.response?.data?.error || 'QR no encontrado.');
      }
    },
    async ensureTicket() {
      if (this.activeTicket) return true;
      this.openOrderModal();
      return false;
    },
    async syncTicketMeta(silent = true) {
      if (!this.activeTicket) return;
      try {
        const payload = {
          service_type: this.orderForm.service_type,
          table_id: this.orderForm.service_type === 'onsite' ? this.orderForm.table_id : null,
          order_label: this.orderForm.order_label || null,
          notes: this.orderForm.notes || null,
        };
        const { data } = await api.put(`/api/pos/tickets/${this.activeTicket.id}`, payload);
        this.activeTicket = data.ticket;
        this.items = data.items || [];
        this.payments = data.payments || [];
        this.emitModuleEvent('pos.ticket.updated', { ticket: data.ticket, origin_channel: 'comandero' });
      } catch (error) {
        if (!silent) this.showError(error.response?.data?.error || 'No se pudo actualizar la orden.');
      }
    },
    selectProduct(product) {
      if (this.stockBlocked(product) || this.posBusy.addProductFromModal) return;
      if (!this.cashSession) return;
      if (!this.activeTicket) {
        this.queuedProduct = product;
        this.openOrderModal();
        return;
      }
      this.productModal = product;
      const selections = {};
      (product.option_groups || []).forEach((group) => {
        const availableOptions = (group.options || []).filter((option) => option.availability_status !== 'sold_out');
        if (group.selection_type === 'multiple') {
          selections[group.id] = availableOptions.filter((option) => option.is_default).map((option) => option.id);
        } else {
          const defaultOption = availableOptions.find((option) => option.is_default) || (group.is_required ? availableOptions[0] : null);
          selections[group.id] = defaultOption ? defaultOption.id : null;
        }
      });
      this.productForm = { quantity: 1, notes: '', selections };
    },
    closeProductModal() {
      this.productModal = null;
      this.productForm = { quantity: 1, notes: '', selections: {} };
    },
    optionSelected(group, option) {
      const selected = this.productForm.selections[group.id];
      return group.selection_type === 'multiple' ? (selected || []).includes(option.id) : Number(selected) === Number(option.id);
    },
    toggleOption(group, option, checked) {
      if (option.availability_status === 'sold_out') return;
      if (group.selection_type === 'multiple') {
        const current = [...(this.productForm.selections[group.id] || [])];
        if (checked && !current.includes(option.id)) current.push(option.id);
        if (!checked && current.includes(option.id)) current.splice(current.indexOf(option.id), 1);
        this.productForm.selections[group.id] = current.filter(Boolean);
      } else {
        this.productForm.selections[group.id] = checked ? option.id : null;
      }
    },
    selectedOptions(product = this.productModal) {
      if (!product) return [];
      const selected = [];
      (product.option_groups || []).forEach((group) => {
        const value = this.productForm.selections[group.id];
        const ids = group.selection_type === 'multiple' ? value || [] : [value].filter(Boolean);
        group.options.filter((option) => option.availability_status !== 'sold_out').forEach((option) => {
          if (ids.map(Number).includes(Number(option.id))) selected.push({ ...option, option_id: option.id, option_code: option.code, group_id: group.id, group_name: group.name, group_code: group.code });
        });
      });
      const rule = this.modifierPricingRule(product);
      if (!rule) return selected;
      const groupCodes = (Array.isArray(rule.group_codes) ? rule.group_codes : []).map((code) => String(code).toLowerCase());
      const includedCount = Math.max(0, Number(rule.included_count || 0));
      const extraPrice = Math.max(0, Number(rule.extra_price || 0));
      let used = 0;
      return selected.map((option) => {
        if (!groupCodes.includes(String(option.group_code || '').toLowerCase())) return option;
        const included = used < includedCount;
        used += 1;
        return { ...option, catalog_price_delta: Number(option.price_delta || 0), price_delta: included ? 0 : extraPrice, included_in_base: included };
      });
    },
    modifierPricingRule(product = this.productModal) {
      const rule = product?.config?.modifier_pricing;
      return rule && rule.type === 'included_count' ? rule : null;
    },
    modifierSelectionError(product = this.productModal) {
      const rule = this.modifierPricingRule(product);
      if (!rule) return '';
      const groupCodes = (Array.isArray(rule.group_codes) ? rule.group_codes : []).map((code) => String(code).toLowerCase());
      const count = this.selectedOptions(product).filter((item) => groupCodes.includes(String(item.group_code || '').toLowerCase())).length;
      const minimum = Math.max(0, Number(rule.min_select_total || 0));
      const maximum = rule.max_select_total === null || rule.max_select_total === undefined ? null : Math.max(0, Number(rule.max_select_total));
      if (count < minimum) return `Selecciona al menos ${minimum} ingrediente.`;
      if (maximum !== null && count > maximum) return `Puedes seleccionar como maximo ${maximum} ingredientes.`;
      return '';
    },
    includedCountLabel(value) {
      const count = Number(value || 0);
      return count === 1 ? '1 ingrediente incluido' : `${count} ingredientes incluidos`;
    },
    optionPriceLabel(group, option, product = this.productModal) {
      const rule = this.modifierPricingRule(product);
      const groupCodes = (Array.isArray(rule?.group_codes) ? rule.group_codes : []).map((code) => String(code).toLowerCase());
      if (rule && groupCodes.includes(String(group.code || '').toLowerCase())) {
        const selectedOption = this.selectedOptions(product).find((item) => Number(item.option_id) === Number(option.id));
        if (selectedOption) return selectedOption.included_in_base ? 'Incluido' : `+${this.money(selectedOption.price_delta)}`;
        const selectedCount = this.selectedOptions(product).filter((item) => groupCodes.includes(String(item.group_code || '').toLowerCase())).length;
        return selectedCount < Number(rule.included_count || 0) ? 'Incluido' : `+${this.money(rule.extra_price)}`;
      }
      return Number(option.price_delta || 0) ? `+${this.money(option.price_delta)}` : 'Sin costo';
    },
    productUnitPrice(product) {
      const modifiersTotal = this.selectedOptions(product).reduce((sum, option) => sum + Number(option.price_delta || 0), 0);
      return Number(product.sale_price || 0) + modifiersTotal;
    },
    async addProductFromModal() {
      if (!this.productModal) return;
      const ok = await this.ensureTicket();
      if (!ok) return;

      const missing = (this.productModal.option_groups || []).find((group) => {
        if (!group.is_required) return false;
        const value = this.productForm.selections[group.id];
        return group.selection_type === 'multiple' ? !(value || []).length : !value;
      });
      if (missing) {
        this.showError(`Selecciona ${missing.name}.`);
        return;
      }
      const modifierError = this.modifierSelectionError(this.productModal);
      if (modifierError) {
        this.showError(modifierError);
        return;
      }

      try {
        const payload = {
          quantity: this.productForm.quantity,
          notes: this.productForm.notes,
          modifiers: this.selectedOptions(),
        };
        if (this.productModal.is_virtual_extra) {
          payload.extra_option_id = this.productModal.extra_option_id;
          payload.name = this.productModal.name;
          payload.unit_price = Number(this.productModal.sale_price || 0);
          payload.modifiers = [];
        } else {
          payload.product_id = this.productModal.id;
        }

        const { data } = await api.post(`/api/pos/tickets/${this.activeTicket.id}/items`, payload);
        this.activeTicket = data.ticket;
        this.items = data.items || [];
        this.payments = data.payments || [];
        this.closeProductModal();
        await this.loadTickets();
        // No se emite nada al PV mientras la orden esta en armado.
        // El PV recibira una sola notificacion cuando se presione Enviar comanda.
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo agregar producto.');
      }
    },
    async removeItemLocal(item) {
      const reason = await promptText('Motivo para quitar producto', 'Corrección desde comandero');
      if (!reason) return;
      const adminPin = this.isPosAdmin ? null : await promptText('PIN de administrador');
      if (!this.isPosAdmin && !adminPin) return;
      try {
        const { data } = await api.delete(`/api/pos/tickets/${this.activeTicket.id}/items/${item.id}`, { data: { reason, admin_pin: adminPin } });
        this.activeTicket = data.ticket;
        this.items = data.items || [];
        this.payments = data.payments || [];
        await this.loadTickets();
        this.emitModuleEvent('pos.ticket.updated', { ticket: data.ticket, origin_channel: 'comandero' });
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo quitar el producto.');
      }
    },
    async sendOrderCommand() {
      if (!this.activeTicket || !this.pendingCommandItems.length) {
        this.showError('No hay productos nuevos pendientes de enviar.');
        return;
      }
      this.sending = true;
      try {
        await this.syncTicketMeta(true);
        const { data } = await api.post(`/api/pos/tickets/${this.activeTicket.id}/send-command`);
        const payload = {
          ticket: this.activeTicket,
          items: this.pendingCommandItems,
          kitchen_tickets: data.kitchen_tickets || [],
          origin_channel: 'comandero',
          created_by: this.$store.state.user?.name || 'Comandero',
          created_at: new Date().toISOString(),
        };
        this.emitModuleEvent('pos.command.created', payload);
        this.emitModuleEvent('pos.ticket.updated', { ticket: this.activeTicket, origin_channel: 'comandero' });
        this.showSummaryModal = false;
        this.showSuccess('Comanda guardada. El indicador muestra cuándo termina de enviarse.');
        this.resetOrder(false);
        await Promise.all([this.loadTickets(), this.loadTables()]);
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo enviar la comanda.');
      } finally {
        this.sending = false;
      }
    },
    async resetOrder(confirmReset = true) {
      if (confirmReset && this.activeItems.length && !await confirmAction('¿Limpiar la orden actual en este dispositivo? La cuenta seguirá abierta en PV.')) return;
      this.activeTicket = null;
      this.items = [];
      this.payments = [];
      this.orderForm = { service_type: 'takeaway', order_label: '', table_id: null, notes: '' };
      this.showSummaryModal = false;
    },
    backToCategories() {
      this.activeCategory = null;
      this.search = '';
    },
    clearSearch() {
      this.search = '';
    },
    focusNextProductInput(event) {
      const modal = event?.target?.closest('.product-modal');
      const textarea = modal?.querySelector('textarea');
      if (textarea) textarea.focus();
    },
    normalizedModifiers(item) {
      if (Array.isArray(item.modifiers)) return item.modifiers;
      try { return JSON.parse(item.modifiers || '[]'); } catch (_error) { return []; }
    },
    ticketLabel(ticket) {
      if (!ticket) return '';
      if (ticket.order_label) return ticket.order_label;
      if (ticket.table_name) return `Mesa ${ticket.table_name}`;
      if (ticket.customer_name) return ticket.customer_name;
      return ticket.service_type === 'takeaway' ? 'Mostrador / Para llevar' : ticket.ticket_number;
    },
    serviceTypeLabel(type) {
      if (type === 'onsite') return 'Mesa';
      if (type === 'delivery') return 'Entrega';
      if (type === 'customer') return 'Cliente / QR';
      return 'Para llevar';
    },
    emitModuleEvent(event, data) {
      if (data?.ticket?._pending || Number(data?.ticket?.id || data?.ticket_id || 0) < 0) return;
      const socket = getOrConnectSocket(this.$store.state.context, this.$store.state.user) || getSocket();
      const context = this.$store.state.context;
      const room = context?.branch?.token ? `branch_${context.branch.token}` : `company_${context?.company?.token}`;
      if (socket && room) socket.emit('moduleEvent', { room, event, data });
    },
    money(value) {
      return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(value || 0));
    },
    shortTime(value) {
      return parsePosDate(value || Date.now()).toLocaleTimeString('es-MX', { timeZone: POS_TIME_ZONE, hour: '2-digit', minute: '2-digit' });
    },
    showSuccess(text) {
      this.message = text;
      this.messageType = 'success';
      window.setTimeout(() => { this.message = ''; }, 3200);
    },
    showError(text) {
      this.message = text;
      this.messageType = 'error';
      window.setTimeout(() => { this.message = ''; }, 5200);
    },
  },
};
</script>

<style lang="scss" scoped>
.commander-screen {
  min-height: 100vh;
  color: var(--gt-text);
  background: #f3f5f8;
  padding-top: 4.75rem;
  padding-bottom: 6.5rem;
}
.commander-topbar {
  position: fixed;
  inset: 0 0 auto 0;
  height: 4.75rem;
  z-index: 900;
  display: grid;
  grid-template-columns: minmax(12rem, auto) minmax(14rem, 1fr) auto;
  align-items: center;
  gap: .75rem;
  padding: .75rem 1rem;
  background: #fff;
  border-bottom: 1px solid var(--gt-border);
  box-shadow: 0 10px 30px rgba(29, 53, 87, .06);
}
.commander-brand,
.commander-actions,
.cash-actions,
.modal-head,
.modal-actions {
  display: flex;
  align-items: center;
  gap: .65rem;
}
.commander-brand strong { display: block; font-size: 1.06rem; }
.commander-brand small { display: block; color: var(--gt-muted); margin-top: .12rem; }
.commander-actions { justify-content: flex-end; flex-wrap: nowrap; min-width: max-content; }
.round-icon {
  width: 2.65rem;
  height: 2.65rem;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 999px;
  background: var(--gt-primary-soft);
  color: var(--gt-primary);
  text-decoration: none;
  font-weight: 950;
  cursor: pointer;
  flex: 0 0 auto;
}
.pos-btn {
  border: 0;
  border-radius: 14px;
  padding: .78rem .95rem;
  font-weight: 950;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.75rem;
}
.pos-btn.primary { background: var(--gt-primary); color: #fff; box-shadow: 0 12px 28px rgba(29, 53, 87, .16); }
.pos-btn.ghost { background: #eef2f7; color: var(--gt-primary); }
.pos-btn.full { width: 100%; }
.pos-btn:disabled { opacity: .48; cursor: not-allowed; }
.accounts-nav-btn {
  position: relative;
  border: 0;
  border-radius: 16px;
  min-width: 3.4rem;
  height: 2.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: .4rem;
  padding: 0 .72rem;
  background: #eef2f7;
  color: var(--gt-primary);
  box-shadow: inset 0 0 0 1px rgba(29,53,87,.10);
  cursor: pointer;
  white-space: nowrap;
}
.accounts-nav-btn .accounts-icon { font-size: 1.18rem; line-height: 1; }
.accounts-nav-btn .accounts-text { font-size: .82rem; font-weight: 950; line-height: 1; }
.accounts-nav-btn strong {
  position: absolute;
  right: -.32rem;
  top: -.32rem;
  min-width: 1.35rem;
  height: 1.35rem;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background: var(--gt-primary);
  color: #fff;
  font-size: .72rem;
  padding: 0 .25rem;
}
.big-action { min-height: 3.7rem; font-size: 1.04rem; }
.status-pill {
  border-radius: 999px;
  padding: .45rem .68rem;
  font-size: .78rem;
  font-weight: 950;
  background: #eef2f7;
  color: var(--gt-muted);
}
.status-pill.ok { background: #e8fbf5; color: #147161; }
.status-pill.warn { background: #fff5df; color: #94600e; }
.order-chip {
  border: 1px solid var(--gt-border);
  background: #f8fafc;
  color: var(--gt-text);
  border-radius: 18px;
  min-height: 3.1rem;
  padding: .5rem .85rem;
  text-align: left;
  cursor: pointer;
  box-shadow: inset 0 1px 0 rgba(255,255,255,.8);
}
.order-chip span,
.order-chip strong { display: block; }
.order-chip span { color: var(--gt-muted); font-size: .76rem; font-weight: 900; }
.order-chip strong { overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
.order-chip.empty { border-color: rgba(29, 53, 87, .22); background: var(--gt-primary-soft); color: var(--gt-primary); }
.sticky-search-wrap {
  position: sticky;
  top: 4.75rem;
  z-index: 760;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: .8rem;
  padding: .75rem 1rem;
  background: rgba(243, 245, 248, .92);
  border-bottom: 1px solid var(--gt-border);
  backdrop-filter: blur(16px);
}
.search-box {
  position: relative;
  display: flex;
  align-items: center;
  gap: .55rem;
  background: #fff;
  border: 1px solid var(--gt-border);
  border-radius: 18px;
  min-height: 3.1rem;
  padding: 0 .5rem 0 .95rem;
  box-shadow: 0 12px 28px rgba(29, 53, 87, .06);
}
.search-box span { color: var(--gt-muted); font-weight: 950; }
.search-box input {
  border: 0;
  outline: 0;
  min-width: 0;
  flex: 1;
  color: var(--gt-text);
  background: transparent;
  font-weight: 850;
}
.search-box button {
  border: 0;
  width: 2.15rem;
  height: 2.15rem;
  border-radius: 999px;
  background: #eef2f7;
  color: var(--gt-primary);
  font-size: 1.3rem;
  font-weight: 900;
  cursor: pointer;
}
.search-context { min-width: 12rem; }
.search-context strong,
.search-context small { display: block; }
.search-context small { color: var(--gt-muted); font-size: .78rem; margin-top: .1rem; }
.commander-toast {
  position: fixed;
  top: 5.25rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1500;
  max-width: min(92vw, 34rem);
  padding: .82rem 1rem;
  border-radius: 999px;
  background: #e8fbf5;
  color: #147161;
  font-weight: 950;
  box-shadow: 0 14px 40px rgba(0,0,0,.12);
}
.commander-toast.error { background: #fff0ec; color: var(--gt-danger); }
.commander-main {
  padding: .9rem 1rem 1.3rem;
}
.commander-main.blocked { filter: blur(1px); pointer-events: none; }
.category-grid,
.product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(8.6rem, 1fr));
  gap: .65rem;
}
.category-tile,
.product-tile {
  border: 1px solid var(--gt-border);
  border-radius: 18px;
  background: #fff;
  color: var(--gt-text);
  text-align: left;
  cursor: pointer;
  box-shadow: 0 12px 30px rgba(29, 53, 87, .06);
  transition: transform .14s ease, box-shadow .14s ease, border-color .14s ease;
}
.category-tile:active,
.product-tile:active { transform: scale(.985); }
.category-tile {
  min-height: 5.7rem;
  padding: .8rem;
}
.category-tile span { display: block; font-weight: 950; font-size: .98rem; }
.category-tile strong { display: inline-block; margin-top: .72rem; color: var(--gt-primary); font-size: 1.35rem; }
.category-tile small { color: var(--gt-muted); margin-left: .25rem; font-weight: 800; }
.product-tile {
  min-height: 5.8rem;
  padding: .78rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: .65rem;
}
.product-tile strong { display: block; font-size: .95rem; line-height: 1.12; }
.product-tile small { display: block; color: var(--gt-muted); font-size: .76rem; font-weight: 760; margin-top: .18rem; }
.product-tile em { font-style: normal; font-weight: 950; color: var(--gt-accent); }
.product-tile.extra { border-color: rgba(212, 132, 68, .42); background: #fffaf5; }
.empty-state {
  border: 1px dashed rgba(31,41,55,.18);
  border-radius: 18px;
  padding: 1rem;
  color: var(--gt-muted);
  text-align: center;
  font-weight: 850;
  background: rgba(255,255,255,.72);
}
.accounts-modal { width: min(52rem, 100%); }
.accounts-toolbar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: .7rem;
  align-items: center;
  margin-bottom: .8rem;
}
.search-box.compact { min-height: 2.8rem; box-shadow: none; }
.accounts-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr));
  gap: .75rem;
  max-height: min(62vh, 34rem);
  overflow: auto;
  padding-right: .15rem;
}
.account-card {
  border: 1px solid var(--gt-border);
  border-radius: 20px;
  background: #fff;
  padding: .85rem;
  box-shadow: 0 12px 28px rgba(29,53,87,.06);
}
.account-card.active { border-color: rgba(29,53,87,.42); box-shadow: 0 16px 36px rgba(29,53,87,.12); }
.account-card header { display: flex; justify-content: space-between; gap: .65rem; align-items: flex-start; }
.account-card header strong { display: block; font-size: 1rem; }
.account-card header small { display: block; color: var(--gt-muted); margin-top: .12rem; font-size: .76rem; }
.account-card header em { font-style: normal; color: var(--gt-accent); font-weight: 950; white-space: nowrap; }
.account-meta { display: flex; flex-wrap: wrap; gap: .38rem; margin: .75rem 0; }
.account-meta span { border-radius: 999px; background: #eef2f7; color: var(--gt-muted); font-size: .75rem; font-weight: 900; padding: .32rem .55rem; }
.account-actions { display: grid; grid-template-columns: 1fr 1.2fr; gap: .55rem; }

.floating-stack {
  position: fixed;
  right: 1.15rem;
  bottom: 1.15rem;
  z-index: 820;
  display: grid;
  justify-items: end;
  gap: .65rem;
  pointer-events: none;
}
.floating-stack > button { pointer-events: auto; }
.floating-back {
  border: 0;
  border-radius: 999px;
  background: #fff;
  color: var(--gt-primary);
  min-height: 3rem;
  padding: .75rem 1rem;
  font-weight: 950;
  box-shadow: 0 16px 36px rgba(29,53,87,.16);
  cursor: pointer;
}
.floating-next {
  border: 0;
  border-radius: 26px;
  min-width: 8.8rem;
  min-height: 4.5rem;
  display: grid;
  grid-template-columns: auto auto;
  grid-template-rows: auto auto;
  align-items: center;
  justify-content: center;
  column-gap: .6rem;
  padding: .7rem 1rem;
  background: var(--gt-primary);
  color: #fff;
  box-shadow: 0 20px 44px rgba(29,53,87,.28);
  cursor: pointer;
}
.floating-next span {
  min-width: 1.8rem;
  height: 1.8rem;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background: rgba(255,255,255,.16);
  font-weight: 950;
}
.floating-next strong { font-size: 1.8rem; line-height: 1; }
.floating-next small { grid-column: 1 / -1; color: rgba(255,255,255,.86); font-weight: 850; }
.floating-next.empty { background: var(--gt-accent); }
.cash-blocker,
.modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: rgba(15, 23, 42, .28);
  backdrop-filter: blur(10px);
}
.cash-card,
.modal {
  width: min(96vw, 48rem);
  max-height: min(92vh, 54rem);
  overflow: auto;
  border-radius: 26px;
  background: #fff;
  color: var(--gt-text);
  border: 1px solid var(--gt-border);
  box-shadow: 0 30px 90px rgba(29,53,87,.22);
  padding: 1.1rem;
}
.cash-card h1 { margin: .8rem 0 .45rem; }
.cash-card p { color: var(--gt-muted); }
.modal-head { justify-content: space-between; margin-bottom: 1rem; }
.modal-head small { color: var(--gt-muted); display: block; font-weight: 900; margin-bottom: .1rem; }
.modal-head h2 { margin: 0; }
.service-picker { display: grid; grid-template-columns: repeat(3, 1fr); gap: .7rem; margin-bottom: 1rem; }
.service-card {
  min-height: 5.8rem;
  border: 1px solid var(--gt-border);
  border-radius: 18px;
  background: #f8fafc;
  color: var(--gt-text);
  text-align: left;
  padding: .9rem;
  cursor: pointer;
}
.service-card.active { background: var(--gt-primary-soft); border-color: rgba(29,53,87,.28); color: var(--gt-primary); }
.service-card strong,
.service-card small { display: block; }
.service-card small { color: var(--gt-muted); margin-top: .24rem; font-weight: 800; }
.service-flow { display: grid; gap: .85rem; }
.service-flow label span,
.product-config label span,
.summary-notes span {
  display: block;
  margin-bottom: .38rem;
  color: var(--gt-muted);
  font-weight: 900;
  font-size: .82rem;
}
.service-flow input,
.customer-search input,
.inline-form input,
.product-config input,
.product-config textarea,
.summary-notes textarea {
  width: 100%;
  border: 1px solid var(--gt-border);
  background: #fff;
  color: var(--gt-text);
  border-radius: 16px;
  padding: .9rem 1rem;
  outline: none;
}
.table-grid,
.customer-list,
.summary-items { display: grid; gap: .65rem; }
.touch-grid { grid-template-columns: repeat(auto-fill, minmax(8rem, 1fr)); }
.table-card,
.customer-card {
  border: 1px solid var(--gt-border);
  background: #fff;
  border-radius: 18px;
  padding: .85rem;
  text-align: left;
  cursor: pointer;
}
.table-card.occupied { background: #fff5df; border-color: rgba(212,132,68,.30); }
.table-card strong,
.table-card small,
.customer-card strong,
.customer-card small { display: block; }
.table-card small,
.customer-card small { color: var(--gt-muted); margin-top: .2rem; font-weight: 800; }
.customer-card { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: .45rem .7rem; }
.customer-card span { grid-row: 1 / span 2; grid-column: 2; align-self: center; color: var(--gt-success); font-weight: 950; }
.customer-search,
.inline-form { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: .55rem; }
.inline-form { grid-template-columns: minmax(0, 1fr) 5rem auto; }
.new-customer { display: grid; gap: .55rem; border-top: 1px solid var(--gt-border); padding-top: .85rem; }
.product-config { display: grid; grid-template-columns: 8rem minmax(0, 1fr); gap: .75rem; margin-bottom: 1rem; }
.product-pricing-note { display: grid; gap: .2rem; margin-bottom: .8rem; padding: .8rem; border: 1px solid rgba(29,53,87,.2); border-radius: 16px; background: var(--gt-primary-soft); color: var(--gt-primary); }
.option-group { border-top: 1px solid var(--gt-border); padding-top: .85rem; margin-top: .85rem; }
.option-title { display: flex; justify-content: space-between; gap: .75rem; margin-bottom: .65rem; }
.option-title small { color: var(--gt-muted); font-weight: 800; }
.option-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(8.5rem, 1fr)); gap: .55rem; }
.option-chip {
  min-height: 3.25rem;
  border: 1px solid var(--gt-border);
  border-radius: 16px;
  background: #f8fafc;
  color: var(--gt-text);
  font-weight: 900;
  cursor: pointer;
}
.option-chip.active { background: var(--gt-primary-soft); color: var(--gt-primary); border-color: rgba(29,53,87,.28); }
.option-chip.unavailable { opacity: .58; cursor: not-allowed; background: #fff1f2; color: #9f1239; text-decoration: line-through; }
.option-chip span,
.option-chip small { display: block; }
.modal-total,
.summary-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 1rem 0;
  padding-top: 1rem;
  border-top: 1px solid var(--gt-border);
}
.modal-total strong,
.summary-total strong { font-size: 1.5rem; }
.summary-meta {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: .65rem;
  margin-bottom: .8rem;
}
.summary-meta div {
  border: 1px solid var(--gt-border);
  background: #f8fafc;
  border-radius: 16px;
  padding: .75rem;
}
.summary-meta span,
.summary-meta strong { display: block; }
.summary-meta span { color: var(--gt-muted); font-size: .78rem; font-weight: 900; }
.summary-item {
  display: flex;
  justify-content: space-between;
  gap: .75rem;
  padding: .82rem;
  border-radius: 18px;
  background: #f8fafc;
  border: 1px solid var(--gt-border);
}
.summary-item strong,
.summary-item small { display: block; }
.summary-item small { color: var(--gt-muted); margin-top: .18rem; }
.summary-item-actions { display: flex; flex-direction: column; align-items: flex-end; gap: .5rem; font-weight: 950; }
.summary-item-actions button {
  border: 0;
  border-radius: 999px;
  width: 2.2rem;
  height: 2.2rem;
  background: #fff0ec;
  color: var(--gt-danger);
  font-size: 1.15rem;
  cursor: pointer;
}
.summary-notes { display: block; margin-top: .8rem; }
.modal-actions { justify-content: flex-end; flex-wrap: wrap; margin-top: .8rem; padding-top: .9rem; border-top: 1px solid var(--gt-border); }
@media (max-width: 980px) {
  .commander-topbar {
    grid-template-columns: auto minmax(0, 1fr) auto;
    padding: .65rem .75rem;
  }
  .commander-actions .status-pill { display: none; }
  .commander-actions .pos-btn.ghost:last-child { display: none; }
  .order-chip { min-width: 0; }
  .sticky-search-wrap { grid-template-columns: 1fr; gap: .45rem; padding: .65rem .75rem; }
  .search-context { min-width: 0; }
  .commander-main { padding: .75rem; }
}
@media (max-width: 680px) {
  .commander-brand small { display: none; }
  .commander-brand strong { font-size: .98rem; }
  .commander-actions .pos-btn { display: none; }
  .accounts-nav-btn { width: 3rem; min-width: 3rem; padding: 0; }
  .accounts-nav-btn .accounts-text { display: none; }
  .category-grid,
  .product-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: .55rem; }
  .service-picker,
  .summary-meta,
  .product-config { grid-template-columns: 1fr; }
  .inline-form,
  .customer-search { grid-template-columns: 1fr; }
.floating-stack { right: .85rem; bottom: .85rem; }
  .floating-next { min-width: 7.6rem; }
}
@media (max-width: 430px) {
  .commander-screen { padding-top: 4.55rem; }
  .commander-topbar { grid-template-columns: auto minmax(0, 1fr) auto; height: 4.55rem; min-height: 4.55rem; padding: .55rem .62rem; gap: .45rem; overflow: hidden; }
  .commander-brand { min-width: 0; gap: .4rem; }
  .commander-brand strong { display: none; }
  .round-icon { width: 2.45rem; height: 2.45rem; }
  .commander-actions { display: flex; grid-column: 3; min-width: 3rem; }
  .commander-actions .status-pill,
  .commander-actions .pos-btn { display: none; }
  .order-chip { grid-column: 2; min-height: 2.65rem; padding: .38rem .58rem; border-radius: 15px; }
  .order-chip span { font-size: .68rem; }
  .order-chip strong { font-size: .82rem; }
  .sticky-search-wrap { top: 4.55rem; }
  .product-tile,
  .category-tile { min-height: 5.35rem; padding: .68rem; }
}

.category-tile,.category-card,.product-tile,.product-card{background:var(--pos-tile-color,#edf2f7) !important;min-height:var(--pos-tile-height,120px) !important;transition:opacity .15s,box-shadow .15s}
.product-tile.stock-empty,.product-card.stock-empty{opacity:.5;filter:saturate(.25)}
.product-tile:disabled,.product-card:disabled{cursor:not-allowed;opacity:.5}
.stock-label{display:block;font-weight:700;font-size:12px;color:#6e4d27}
.option-row-edit{display:flex!important;flex-wrap:wrap;gap:8px}.option-row-edit input:not([type=checkbox]),.option-row-edit select{flex:1;min-width:110px}
button[aria-busy=true]{cursor:wait;opacity:.6}
</style>
